
import React, { useState, useEffect, useRef } from 'react';
import { db } from '../services/firebase';
import { collection, addDoc, onSnapshot, query, where, deleteDoc, doc } from 'firebase/firestore';
import { UserRole } from '../types';
import { put } from '@vercel/blob';

interface ResourceManagerProps {
  user: any;
  initialSubjectId?: string;
  onBack?: () => void;
}

const BLOB_TOKEN = "vercel_blob_rw_zNodi0FoXXYvFHHJ_0ONOJAzweaSuTBnGC2ic3PMNNMUcAi";

const ResourceManager: React.FC<ResourceManagerProps> = ({ user, initialSubjectId = '', onBack }) => {
  const [resources, setResources] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  
  // Selection State
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState(initialSubjectId);
  
  // Form State
  const [title, setTitle] = useState('');
  const [type, setType] = useState('pdf');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');
  const [activeMedia, setActiveMedia] = useState<any>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const unsubscribeClasses = onSnapshot(collection(db, 'classes'), (snap) => {
      setClasses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    let subQ;
    if (user.role === UserRole.STUDENT) {
        if (user.classId) {
            subQ = query(collection(db, 'subjects'), where('classId', '==', user.classId));
        } else {
            setSubjects([]);
            return unsubscribeClasses;
        }
    } else {
        subQ = collection(db, 'subjects');
    }

    const unsubscribeSubjects = onSnapshot(subQ, (snap) => {
        setSubjects(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    
    const unsubscribeResources = onSnapshot(collection(db, 'resources'), (snap) => {
      setResources(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => {
        unsubscribeClasses();
        unsubscribeSubjects();
        unsubscribeResources();
    };
  }, [user]);

  const filteredSubjectsForForm = subjects.filter(s => s.classId === selectedClassId);

  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClassId) return alert("Please select a target Class first.");
    if (!selectedSubjectId) return alert("Please select a Subject category.");
    if (!fileInputRef.current?.files?.[0]) return alert("Please select a file to upload.");

    const file = fileInputRef.current.files[0];
    
    try {
      setIsUploading(true);
      setUploadStage('Preparing Cloud Sync...');
      setUploadProgress(5);

      // Animation for the "preparing" feel
      await new Promise(r => setTimeout(r, 500));
      setUploadProgress(15);
      setUploadStage('Uploading to Secure Vault...');

      const blob = await put(file.name, file, {
        access: 'public',
        token: BLOB_TOKEN,
      });

      setUploadProgress(80);
      setUploadStage('Finalizing Metadata...');

      await addDoc(collection(db, 'resources'), { 
          title: title || file.name, 
          url: blob.url, 
          type: type, 
          fileName: file.name,
          subjectId: selectedSubjectId,
          classId: selectedClassId,
          ownerId: user.uid,
          createdAt: Date.now()
      });

      setUploadProgress(100);
      setUploadStage('Successfully Published!');
      
      setTimeout(() => {
        setTitle('');
        setShowAddForm(false);
        setSelectedClassId('');
        setSelectedSubjectId('');
        setIsUploading(false);
        setUploadProgress(0);
        setUploadStage('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      }, 1500);

    } catch (err) {
      console.error("Upload error:", err);
      alert("Cloud upload failed. Check your internet connection.");
      setIsUploading(false);
    }
  };

  const getAcceptType = () => {
    if (type === 'video') return "video/mp4,video/x-m4v,video/*";
    if (type === 'pdf') return ".pdf,application/pdf";
    if (type === 'image') return "image/*";
    return "*";
  };

  const visibleResources = resources.filter(r => {
    const matchesClass = user.role === UserRole.STUDENT ? r.classId === user.classId : true;
    const matchesSubject = selectedSubjectId ? r.subjectId === selectedSubjectId : true;
    
    if (showAddForm) return true;
    return matchesClass && matchesSubject;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white dark:bg-dark-card p-6 rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm">
        <div className="flex items-center gap-4">
            {user.role === UserRole.STUDENT && (
                <button onClick={onBack} className="w-10 h-10 rounded-xl bg-gray-50 dark:bg-gray-800 flex items-center justify-center text-gray-500 hover:bg-purple-600 hover:text-white transition-all">
                    <i className="fas fa-arrow-left"></i>
                </button>
            )}
            <div>
                <h2 className="text-2xl font-black dark:text-white tracking-tight">Cloud Materials</h2>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                    {user.role === UserRole.STUDENT ? 'My Classroom Library' : 'Staff Resource Manager'}
                </p>
            </div>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
            {user.role !== UserRole.STUDENT && (
                <button 
                  onClick={() => setShowAddForm(!showAddForm)}
                  className={`px-6 py-3 rounded-xl font-bold text-sm transition-all flex items-center gap-2 shadow-lg ${showAddForm ? 'bg-gray-100 text-gray-600 dark:bg-gray-800' : 'bg-purple-600 text-white shadow-purple-200 dark:shadow-none hover:bg-purple-700'}`}
                >
                  <i className={`fas ${showAddForm ? 'fa-times' : 'fa-plus-circle'}`}></i>
                  {showAddForm ? 'Cancel' : 'Add Material'}
                </button>
            )}
            
            {!showAddForm && (
                <select 
                  className="flex-1 md:flex-none bg-gray-50 dark:bg-gray-800 border-none p-3 rounded-xl dark:text-white text-xs font-bold outline-none ring-1 ring-gray-200 dark:ring-gray-700 focus:ring-purple-500" 
                  value={selectedSubjectId} 
                  onChange={e => setSelectedSubjectId(e.target.value)}
                >
                    <option value="">All Subjects</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name} ({classes.find(c => c.id === s.classId)?.name})</option>)}
                </select>
            )}
        </div>
      </div>

      {showAddForm && user.role !== UserRole.STUDENT && (
        <div className="bg-white dark:bg-dark-card p-8 rounded-[2.5rem] border-2 border-purple-500/30 dark:border-purple-500/10 shadow-2xl animate-fadeIn space-y-8 relative overflow-hidden">
          {/* Detailed Progress UI Overlay during upload */}
          {isUploading && (
            <div className="absolute inset-0 z-50 bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-md flex flex-col items-center justify-center p-8 animate-fadeIn">
              <div className="w-full max-w-md space-y-6 text-center">
                <div className="relative inline-block">
                  <div className="w-24 h-24 rounded-full border-4 border-gray-100 dark:border-gray-800 flex items-center justify-center">
                    <span className="text-xl font-black dark:text-white">{uploadProgress}%</span>
                  </div>
                  <svg className="absolute top-0 left-0 w-24 h-24 -rotate-90">
                    <circle 
                      cx="48" cy="48" r="44" 
                      fill="transparent" 
                      stroke="currentColor" 
                      strokeWidth="4" 
                      className="text-purple-600 transition-all duration-500" 
                      strokeDasharray="276.46" 
                      strokeDashoffset={276.46 - (276.46 * uploadProgress) / 100}
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-black dark:text-white mb-1">{uploadStage}</h4>
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest animate-pulse">Establishing Cloud Sync...</p>
                </div>
                <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-600 to-indigo-600 transition-all duration-500 shadow-[0_0_10px_rgba(139,92,246,0.5)]" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-[10px] font-black text-gray-400 uppercase tracking-tighter">
                  <span>Packet Transmission</span>
                  <span>{uploadProgress === 100 ? 'Secure' : 'In Progress'}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-purple-600 flex items-center justify-center text-white shadow-lg">
                  <i className="fas fa-folder-plus text-xl"></i>
              </div>
              <div>
                  <h3 className="text-xl font-black dark:text-white">New Resource Portal</h3>
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Publish to cloud storage</p>
              </div>
          </div>

          <form onSubmit={handleFileUpload} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="space-y-5 bg-gray-50 dark:bg-gray-800/40 p-6 rounded-3xl border border-gray-100 dark:border-gray-800">
                <div className="space-y-2">
                    <label className="text-[10px] font-black text-purple-600 dark:text-purple-400 uppercase tracking-widest ml-1">Step 1: Target Class</label>
                    <select 
                      required 
                      className="w-full bg-white dark:bg-dark-card p-4 rounded-2xl dark:text-white font-bold outline-none border border-gray-100 dark:border-gray-700 focus:ring-2 focus:ring-purple-500/20" 
                      value={selectedClassId} 
                      onChange={e => { setSelectedClassId(e.target.value); setSelectedSubjectId(''); }}
                    >
                        <option value="">Choose Class (Academic Room)</option>
                        {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>

                <div className="space-y-2">
                    <label className="text-[10px] font-black text-purple-600 dark:text-purple-400 uppercase tracking-widest ml-1">Step 2: Select Subject</label>
                    <select 
                      required 
                      disabled={!selectedClassId}
                      className="w-full bg-white dark:bg-dark-card p-4 rounded-2xl dark:text-white font-bold outline-none border border-gray-100 dark:border-gray-700 focus:ring-2 focus:ring-purple-500/20 disabled:opacity-40 transition-opacity" 
                      value={selectedSubjectId} 
                      onChange={e => setSelectedSubjectId(e.target.value)}
                    >
                        <option value="">Choose Subject category...</option>
                        {filteredSubjectsForForm.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>
            </div>

            <div className="space-y-5 bg-gray-50 dark:bg-gray-800/40 p-6 rounded-3xl border border-gray-100 dark:border-gray-800">
                <div className="space-y-2">
                    <label className="text-[10px] font-black text-purple-600 dark:text-purple-400 uppercase tracking-widest ml-1">Step 3: Title & Type</label>
                    <input required placeholder="E.g. Chapter 1 Notes" className="w-full bg-white dark:bg-dark-card p-4 rounded-2xl dark:text-white font-bold outline-none border border-gray-100 dark:border-gray-700 focus:ring-2 focus:ring-purple-500/20" value={title} onChange={e => setTitle(e.target.value)} />
                </div>
                <div className="flex gap-3">
                    {['pdf', 'video', 'image'].map(mType => (
                        <button 
                            key={mType}
                            type="button"
                            onClick={() => setType(mType)}
                            className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${type === mType ? 'bg-purple-600 text-white border-purple-600 shadow-md' : 'bg-white dark:bg-dark-card text-gray-400 border-gray-100 dark:border-gray-700'}`}
                        >
                            {mType}
                        </button>
                    ))}
                </div>
            </div>

            <div className="space-y-5 bg-gray-50 dark:bg-gray-800/40 p-6 rounded-3xl border border-gray-100 dark:border-gray-800 flex flex-col justify-between">
                <div className="space-y-2">
                    <label className="text-[10px] font-black text-purple-600 dark:text-purple-400 uppercase tracking-widest ml-1">Step 4: Choose File</label>
                    <div className="relative group">
                        <input required type="file" ref={fileInputRef} accept={getAcceptType()} className="hidden" onChange={() => setTitle(prev => prev || fileInputRef.current?.files?.[0]?.name || '')} />
                        <button type="button" onClick={() => fileInputRef.current?.click()} className="w-full bg-white dark:bg-dark-card border-2 border-dashed border-gray-200 dark:border-gray-700 p-8 rounded-2xl text-xs font-bold text-gray-400 hover:border-purple-500 hover:text-purple-500 transition-all flex flex-col items-center justify-center gap-3">
                            <i className={`fas ${type === 'video' ? 'fa-clapperboard' : type === 'image' ? 'fa-camera' : 'fa-file-pdf'} text-3xl`}></i>
                            <span className="truncate max-w-full font-mono text-[10px]">{fileInputRef.current?.files?.[0] ? fileInputRef.current.files[0].name : "Select Local Media"}</span>
                        </button>
                    </div>
                </div>

                <button 
                  type="submit" 
                  disabled={!selectedSubjectId} 
                  className={`w-full py-5 text-white font-black rounded-2xl shadow-xl transition-all flex items-center justify-center gap-3 uppercase tracking-widest text-sm ${!selectedSubjectId ? 'bg-gray-300 cursor-not-allowed shadow-none' : 'bg-purple-600 hover:bg-purple-700 shadow-purple-200 dark:shadow-none'}`}
                >
                  <i className="fas fa-cloud-arrow-up"></i> Publish Material
                </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {visibleResources.map(r => (
          <div key={r.id} className="bg-white dark:bg-dark-card p-6 rounded-[2.5rem] border border-gray-100 dark:border-gray-800 flex flex-col justify-between shadow-sm hover:shadow-xl transition-all group overflow-hidden relative">
            {r.type === 'video' && (
                <div className="absolute top-4 right-4">
                    <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center">
                        <i className="fas fa-play text-[10px] text-red-500 animate-pulse"></i>
                    </div>
                </div>
            )}
            <div className="flex flex-col gap-4 mb-6">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white ${r.type === 'video' ? 'bg-red-500 shadow-red-200' : r.type === 'pdf' ? 'bg-orange-500 shadow-orange-200' : 'bg-blue-500 shadow-blue-200'} shadow-lg group-hover:rotate-6 transition-transform`}>
                <i className={`fas ${r.type === 'video' ? 'fa-play' : r.type === 'pdf' ? 'fa-file-pdf' : 'fa-image'} text-xl`}></i>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-[8px] font-black bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 px-2 py-0.5 rounded-full uppercase tracking-widest">
                        {subjects.find(s => s.id === r.subjectId)?.name || 'Resource'}
                    </span>
                    <span className="text-[8px] font-black bg-gray-50 dark:bg-gray-800 text-gray-400 px-2 py-0.5 rounded-full uppercase tracking-widest">
                        {classes.find(c => c.id === r.classId)?.name || 'Class'}
                    </span>
                </div>
                <h4 className="font-black dark:text-white text-base leading-tight group-hover:text-purple-600 transition-colors line-clamp-2">{r.title}</h4>
                <p className="text-[9px] text-gray-400 mt-2 font-mono truncate opacity-60 italic">{r.fileName}</p>
              </div>
            </div>
            <div className="flex gap-2 pt-4 border-t border-gray-50 dark:border-gray-800">
              <button 
                onClick={() => setActiveMedia(r)}
                className="flex-1 text-center py-3 bg-purple-50 dark:bg-gray-800 text-purple-600 dark:text-purple-400 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-purple-600 hover:text-white transition-all flex items-center justify-center gap-2"
              >
                <i className={`fas ${r.type === 'video' ? 'fa-clapperboard' : r.type === 'image' ? 'fa-eye' : 'fa-external-link-alt'} text-[11px]`}></i>
                {r.type === 'video' ? 'Play' : r.type === 'image' ? 'View' : 'Open'}
              </button>
              {user.role !== UserRole.STUDENT && (
                <button onClick={() => deleteDoc(doc(db, 'resources', r.id))} className="px-4 text-red-400 hover:text-white hover:bg-red-500 bg-red-50 dark:bg-red-900/10 rounded-xl transition-all">
                    <i className="fas fa-trash-can text-sm"></i>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {activeMedia && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 lg:p-12 animate-fadeIn">
            <div className="absolute inset-0 bg-black/90 backdrop-blur-xl" onClick={() => setActiveMedia(null)}></div>
            <div className="relative w-full max-w-6xl bg-[#080808] rounded-[3rem] overflow-hidden shadow-2xl border border-white/10 flex flex-col ring-1 ring-white/20">
                <div className="absolute top-8 right-8 z-50">
                    <button onClick={() => setActiveMedia(null)} className="w-12 h-12 rounded-full bg-white/10 hover:bg-red-500 text-white flex items-center justify-center transition-all backdrop-blur-md">
                        <i className="fas fa-times text-xl"></i>
                    </button>
                </div>
                
                <div className="p-8 bg-gradient-to-b from-black/90 to-transparent absolute top-0 left-0 w-full z-40 pointer-events-none">
                    <div className="flex items-center gap-3 mb-2">
                        <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"></span>
                        <p className="text-purple-400 text-[10px] font-black uppercase tracking-widest">
                            {activeMedia.type === 'video' ? 'Native Video Stream' : 'Cloud Visualized Material'}
                        </p>
                    </div>
                    <h3 className="text-white text-2xl font-black">{activeMedia.title}</h3>
                </div>

                <div className="flex-1 flex items-center justify-center min-h-[400px] lg:min-h-[650px] bg-[#000]">
                    {activeMedia.type === 'video' ? (
                        <video 
                            src={activeMedia.url} 
                            controls 
                            autoPlay 
                            className="max-w-full max-h-[85vh] outline-none"
                            controlsList="nodownload"
                        ></video>
                    ) : activeMedia.type === 'image' ? (
                        <img 
                            src={activeMedia.url} 
                            className="max-w-full max-h-[85vh] object-contain shadow-2xl" 
                            alt={activeMedia.title} 
                        />
                    ) : (
                        <iframe 
                            src={activeMedia.url} 
                            className="w-full h-full min-h-[75vh] border-none"
                            title={activeMedia.title}
                        ></iframe>
                    )}
                </div>
            </div>
        </div>
      )}
      
      {visibleResources.length === 0 && (
          <div className="text-center py-32 bg-white dark:bg-dark-card rounded-[3rem] border-2 border-dashed border-gray-100 dark:border-gray-800 shadow-inner">
              <div className="w-20 h-20 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="fas fa-folder-open text-3xl text-gray-200"></i>
              </div>
              <h3 className="text-xl font-black dark:text-white">Vault is Empty</h3>
              <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px] mt-2">No learning materials found here.</p>
          </div>
      )}
    </div>
  );
};

export default ResourceManager;
